import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';


import useGateway from 'app/model/Gateway';
import { LocalizationContext } from 'app/localization/Context';

import FullPageTransition from '../general-components/FullPageTransition';
import ChooseHomeType from './ChooseHomeType';
import { AskHomeProfile, AskProfile } from './Profile';
import HomeList from './HomeList';

import GetNumber, { AskVerificationCode } from './stages/GetNumber';
import Connecting, { NewHomeProcessLoading } from './stages/Connecting';
import Waiting from './stages/Waiting';
// import NotFound from './stages/NotFound';
import { FirstContainer } from '../Home';

import './NewHome.scss'
import '../Background.scss'

export default function NewHome({ homesManager, profile, windowWidth, windowHeight }) {
    const inWideView = windowWidth >= 800;
    const height = inWideView ? "var(--window-height)" : undefined;

    const [homeType, setHomeType] = useState(undefined);

    const hasAnyHome = homesManager.homes.length > 0;
    const dismiss = useCallback(() => {
        setHomeType(undefined);
        homesManager.dismissAddHomeProcess();
        // if (hasAnyHome) {
        //     homesManager.dismissAddHomeProcess();
        // } else {
        //     setHomeType(undefined);
        // }
    }, [homesManager]);

    return <Container inWideView={inWideView} windowWidth={windowWidth} windowHeight={windowHeight}>
        <FullPageTransition disableOnFirstTime height={height}>
            <ChooseHomeType
                showWelcome={!hasAnyHome}
                setHomeType={setHomeType}
                canDismiss={homesManager.activeHome !== undefined}
                dismiss={dismiss} // used when (!showWelcome)

                isVisible={homeType === undefined}
                key="newhome-choose-home-type" />

            <NewSolo
                dismiss={dismiss}
                homesManager={homesManager}
                profile={profile}

                isVisible={homeType === "solo"}
                key="newhome-solo" />

            <NewHub
                dismiss={dismiss}
                homesManager={homesManager}
                profile={profile}

                isVisible={homeType === "hub"}
                key="newhome-hub" />
        </FullPageTransition>
    </Container>;
}

function Container({ inWideView, windowWidth, windowHeight, children }) {
    if (!inWideView) {
        return children;
    } else {
        return (
            <FirstContainer windowWidth={windowWidth} windowHeight={windowHeight}>
                <div /> {/* placeholder of connection status */}
                <div className="second-container">
                    <div className="bg-primary-color" />
                    <div className="overlay-page-super-container-blur-back" />
                    <div className="overlay-page-super-container">
                        {children}
                    </div>
                </div>
            </FirstContainer>
        );
    }
}

function NewSolo({ profile, homesManager, dismiss }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    const isSoloLoggedInAtTheStartOfTheProcess = useState(homesManager.isSoloLoggedIn)[0];
    const inOrdinaryAddHomeProcessAtTheStartOfTheProcess = useState(homesManager.inOrdinaryAddHomeProcess)[0];
    const [confirmedAddNewHome, setConfirmedAddNewHome] = useState(false);
    const userWantToAddNewHome =
        confirmedAddNewHome ||
        (inOrdinaryAddHomeProcessAtTheStartOfTheProcess &&
            isSoloLoggedInAtTheStartOfTheProcess);
    // console.log("userWantToAddNewHome:", confirmedAddNewHome, inOrdinaryAddHomeProcessAtTheStartOfTheProcess, isSoloLoggedInAtTheStartOfTheProcess);

    const [phoneNumber, _setPhoneNumber] = useState(undefined);
    const [shouldGetCode, setShouldGetCode] = useState(true);
    const [thereWasErrorForCode, setThereWasErrorForCode] = useState(false);
    const [newHomeName, setNewHomeName] = useState(undefined);
    const [newHomeAvatar, setNewHomeAvatar] = useState(undefined);
    const [gotUserProfile, setGotUserProfile] = useState(false);
    const [newHomeId, setNewHomeId] = useState(undefined);

    const clearPhoneNumber = useCallback(() => {
        _setPhoneNumber(undefined);
        setShouldGetCode(true);
        setThereWasErrorForCode(false);
        setNewHomeName(undefined);
        setNewHomeAvatar(undefined);
        setGotUserProfile(false);
        setNewHomeId(undefined);
        homesManager.logoutFromSolo();
    }, [homesManager]);

    // reload homes if needed
    const _homesManager_isSoloLoggedIn = homesManager.isSoloLoggedIn;
    const _homesManager_isSoloHomesLoaded = homesManager.isSoloHomesLoaded;
    const _homesManager_reloadSoloHomes = homesManager.reloadSoloHomes;
    useEffect(() => {
        if (_homesManager_isSoloLoggedIn) {
            if (!_homesManager_isSoloHomesLoaded) {
                _homesManager_reloadSoloHomes();
            }
        }
    }, [_homesManager_isSoloLoggedIn, _homesManager_isSoloHomesLoaded, _homesManager_reloadSoloHomes]);

    // switch to new home
    const _homesManager_homes = homesManager.homes;
    useEffect(() => {
        if (newHomeId !== undefined) {
            const theNewHomeInList = _homesManager_homes.filter(h => h.home_id === newHomeId)
            if (theNewHomeInList.length > 0) {
                theNewHomeInList[0].switch();
                dismiss();
                setTimeout(() => {
                    window.location.reload(); // reload after switch to clear memory (and ws connection)
                }, 1000);
            }
        }
    }, [_homesManager_homes, newHomeId, dismiss]);

    const stage = useMemo(() => {
        if (homesManager.isSoloLoggedIn) {
            if (homesManager.isSoloHomesLoaded) {
                if (userWantToAddNewHome) {
                    if ((newHomeName === undefined) || (newHomeAvatar === undefined)) {
                        return "ask-new-solo-home-profile";
                    } else {
                        if (!gotUserProfile) {
                            return "ask-user-profile";
                        } else {
                            if (newHomeId === undefined) {
                                return "loading-create-home";
                            } else {
                                return "loading-switch-home";
                            }
                        }
                    }
                } else {
                    return "home-list";
                }
            } else {
                return "loading-homes";
            }
        } else {
            if (phoneNumber !== undefined) {
                if (homesManager.hasLoginTempId) {
                    if (shouldGetCode) {
                        return "get-code";
                    } else {
                        return "loading-verifycode";
                    }
                } else {
                    return "loading-sendcode";
                }
            } else { // it may have a tempId for another phonenumber
                return "get-number";
            }
        }
    }, [homesManager, phoneNumber, userWantToAddNewHome, shouldGetCode,
        newHomeName, newHomeAvatar, gotUserProfile, newHomeId]);

    const setPhoneNumber = useCallback((pn) => {
        homesManager.soloSendSMS(pn, l.fullLanguage);
        _setPhoneNumber(pn);
    }, [homesManager, l.fullLanguage]);

    const checkCode = useCallback((code) => {
        setShouldGetCode(false);
        homesManager.soloVerifyToken(code, (authToken) => { // ok
            // homesManager.reloadSoloHomesWithAuthToken(authToken);
        }, () => { // error
            setShouldGetCode(true);
            setThereWasErrorForCode(true);
        });
    }, [homesManager]);

    const setHomeProfile = useCallback((name, avatar) => {
        setNewHomeName(name);
        setNewHomeAvatar(avatar);
    }, []);

    const submitUserProfile = useCallback((name, avatar, language) => {
        setGotUserProfile(true);

        homesManager.addSolo({
            name: newHomeName,
            avatar: newHomeAvatar,
            client_name: name,
            client_avatar: avatar,
            client_language: language,
        }, (homeId) => { // ok
            setNewHomeId(homeId); // will switch
        }, () => { // error
            dismiss();
        });
    }, [newHomeName, newHomeAvatar, dismiss, homesManager]);

    return (
        <FullPageTransition disableOnFirstTime>
            <RegisterHomeContainer key="newsolo-get-number" isVisible={stage === "get-number"} >
                <div className="bg-secondary-color" />
                <GetNumber
                    homeType="solo"
                    setPhoneNumber={setPhoneNumber}
                    switchToHubIfExists={undefined}
                    canDismiss={true}
                    dismiss={dismiss} />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newsolo-get-code" isVisible={stage === "get-code"} >
                <div className="bg-secondary-color" />
                <AskVerificationCode
                    thereWasErrorForCode={thereWasErrorForCode}
                    phoneNumber={phoneNumber}
                    checkCode={checkCode}
                    resend={homesManager.soloResendSMS}
                    dismiss={dismiss} />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newsolo-loading"
                isVisible={stage.startsWith("loading-")} >
                <div className="bg-secondary-color" />
                <NewHomeProcessLoading
                    loadingText={
                        (stage === "loading-sendcode") ?
                            s.sendingVerificationCode :
                            (stage === "loading-verifycode") ?
                                s.verifyingTheCode :
                                (stage === "loading-homes") ?
                                    s.loadingSoloHomes :
                                    (stage === "loading-create-home") ?
                                        s.creatingSoloHome :
                                        (stage === "loading-switch-home") ?
                                            s.switchingSoloHome : undefined}
                    // clearPhoneNumber={clearPhoneNumber}
                    clearPhoneNumber={
                        (stage === "loading-create-home") ? undefined : clearPhoneNumber}
                    canDismiss={true} dismiss={dismiss}
                />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newsolo-home-list" isVisible={stage === "home-list"} >
                <div className="bg-secondary-color" />
                <HomeList
                    homes={homesManager.homes}
                    activeHome={homesManager.activeHome}
                    reloadSoloHomes={homesManager.reloadSoloHomes}
                    onAddHomeClick={() => setConfirmedAddNewHome(true)}
                    isSoloLoggedIn={homesManager.isSoloLoggedIn}
                    logoutFromSolo={() => {
                        dismiss();
                        homesManager.logoutFromSolo();
                    }}
                    customDismiss={dismiss} />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newsolo-ask-new-solo-home-profile" isVisible={stage === "ask-new-solo-home-profile"} >
                <div className="bg-secondary-color" />
                <AskHomeProfile
                    submitHomeProfile={setHomeProfile}
                    dismiss={dismiss} />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newsolo-ask-user-profile" isVisible={stage === "ask-user-profile"} >
                <div className="bg-secondary-color" />
                <AskProfile
                    profile={profile}
                    submitProfile={submitUserProfile} useTickForSubmitIcon={true}
                    canDismiss={true}
                    dismiss={dismiss} />
            </RegisterHomeContainer>
        </FullPageTransition>
    )
}

function NewHub({ homesManager, profile, dismiss }) {
    const [stage, setStage] = useState("get-number");

    const [registered, setRegistered] = useState(false);
    const notifyRegistered = () => {
        setRegistered(true);
    };
    const [authenticated, setAuthenticated] = useState(false);
    const notifyAuthenticated = () => {
        setAuthenticated(true);
    };

    const [home, setHome] = useState({
        readyForConnection: false,
        phoneNumber: undefined,
        authToken: undefined,
        notifyRegistered,
        notifyAuthenticated,
    });

    const [profileAsked, setProfileAsked] = useState(false);
    const [phoneNumber, _setPhoneNumber] = useState(undefined);

    const addHub = homesManager.addHub;
    const dismissAddHomeProcess = homesManager.dismissAddHomeProcess;
    useEffect(() => {
        if (phoneNumber) {
            if (profileAsked) {
                if (!registered && !authenticated) {
                    setStage("connecting");
                } else {
                    if (!authenticated) {
                        setStage("waiting");
                    } else {
                        addHub(home.phoneNumber, home.authToken);
                        dismissAddHomeProcess();
                    }
                }
            } else {
                setStage("ask-profile");
            }
        } else {
            setStage("get-number");
        }
    }, [home, registered, authenticated, addHub, phoneNumber, profileAsked, dismissAddHomeProcess])

    const gateway = useGateway({ activeHome: home }, profile);
    const dispatchReconnection = gateway.dispatchReconnection;
    const genAuthToken = homesManager.genAuthToken;

    const setPhoneNumber = useCallback((pn) => {
        _setPhoneNumber(pn);
    }, []);

    const onAskProfile = useCallback(() => {
        setProfileAsked(true);
        setHome({
            readyForConnection: true,
            phoneNumber: phoneNumber,
            authToken: genAuthToken(phoneNumber),
            notifyRegistered,
            notifyAuthenticated,
        });
        dispatchReconnection();
    }, [phoneNumber, dispatchReconnection, genAuthToken]);

    const clearPhoneNumber = useCallback(() => {
        _setPhoneNumber(undefined);
        setProfileAsked(false);
        setHome({
            readyForConnection: false,
            phoneNumber: undefined,
            authToken: undefined,
            notifyRegistered,
            notifyAuthenticated,
        });
        setRegistered(false);
        setAuthenticated(false);
        dispatchReconnection();
    }, [dispatchReconnection]);

    // return <WelcomePage />;

    return (
        <FullPageTransition disableOnFirstTime>
            <RegisterHomeContainer key="newhub-get-number" isVisible={stage === "get-number"} >
                <div className="bg-secondary-color" />
                <GetNumber
                    homeType="hub"
                    setPhoneNumber={setPhoneNumber}
                    switchToHubIfExists={homesManager.switchToHubIfExists}
                    canDismiss={true}
                    dismiss={dismiss} />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newhub-ask-profile" isVisible={stage === "ask-profile"} >
                <div className="bg-secondary-color" />
                <AskProfile
                    profile={profile}
                    submitProfile={onAskProfile}
                    canDismiss={true}
                    dismiss={dismiss} />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newhub-connecting" isVisible={stage === "connecting"} >
                <div className="bg-secondary-color" />
                <Connecting clearPhoneNumber={clearPhoneNumber} />
            </RegisterHomeContainer>

            <RegisterHomeContainer key="newhub-waiting" isVisible={stage === "waiting"} >
                <div className="bg-waiting-color" />
                <Waiting clearPhoneNumber={clearPhoneNumber} />
            </RegisterHomeContainer>
        </FullPageTransition>

        /* <FilteredShow value="not-found" variable={stage}>
                <div className="bg-failure-color" />
                <NotFound />
            </FilteredShow> */
    )
}

function RegisterHomeContainer({ children }) {
    const l = useContext(LocalizationContext);

    return (
        <div className="register-home-page"
            dir={l.isRTL ? "rtl" : "ltr"}
            lang={l.language}>
            <div className="bg-blank" />
            {children}
        </div>
    )
}